import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Wifi, 
  Server, 
  Clock,
  Zap,
  RefreshCw,
  Globe,
  Info
} from "lucide-react";

const Status = () => {
  const [status, setStatus] = useState({
    status: "unknown",
    last_seen: null,
    ping: null,
    uptime: null,
    guilds: null,
    checks: {
      health_endpoint: false,
      database_heartbeat: false
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchStatus = async () => {
    try {
      const response = await fetch('/api/bot-status');
      const data = await response.json();
      setStatus(data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch bot status:', error);
      setStatus({
        status: "unknown",
        error: "Failed to fetch status",
        checks: {
          health_endpoint: false,
          database_heartbeat: false
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = () => {
    switch (status.status) {
      case "online":
        return {
          bg: "from-green-500/20 to-emerald-500/20",
          border: "border-green-500/30",
          text: "text-green-400",
          icon: CheckCircle
        };
      case "offline":
        return {
          bg: "from-red-500/20 to-red-600/20",
          border: "border-red-500/30",
          text: "text-red-400",
          icon: XCircle
        };
      default:
        return {
          bg: "from-yellow-500/20 to-orange-500/20",
          border: "border-yellow-500/30",
          text: "text-yellow-400",
          icon: AlertCircle
        };
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds) return "Unknown";
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const statusColors = getStatusColor();
  const StatusIcon = statusColors.icon;

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial="initial"
          animate="animate"
          variants={stagger}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Activity className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Bot Status
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Real-time monitoring of Voxel's operational status and performance metrics
            </p>
          </motion.div>

          {/* Main Status Card */}
          <motion.div variants={fadeInUp} className="mb-8">
            <div className={`bg-gradient-to-r ${statusColors.bg} backdrop-blur-sm border ${statusColors.border} rounded-2xl p-8`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <StatusIcon className={`w-12 h-12 ${statusColors.text}`} />
                  <div>
                    <h2 className="text-3xl font-bold text-white capitalize">
                      {isLoading ? "Checking..." : status.status}
                    </h2>
                    <p className="text-gray-300">
                      Voxel Discord Bot
                    </p>
                  </div>
                </div>
                <button
                  onClick={fetchStatus}
                  disabled={isLoading}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <span className="text-sm text-gray-400">Last Seen</span>
                  </div>
                  <p className="text-white font-semibold">
                    {formatLastSeen(status.last_seen)}
                  </p>
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm text-gray-400">Ping</span>
                  </div>
                  <p className="text-white font-semibold">
                    {status.ping ? `${status.ping}ms` : "Unknown"}
                  </p>
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Activity className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-gray-400">Uptime</span>
                  </div>
                  <p className="text-white font-semibold">
                    {formatUptime(status.uptime)}
                  </p>
                </div>

                <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Server className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-gray-400">Servers</span>
                  </div>
                  <p className="text-white font-semibold">
                    {status.guilds || "Unknown"}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Status Checks */}
          <motion.div variants={fadeInUp} className="mb-8">
            <h3 className="text-2xl font-bold mb-6">Status Information</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Operational</h4>
                <p className="text-sm text-gray-400">
                  Bot is online and responding to commands normally
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-6 h-6 text-yellow-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Degraded</h4>
                <p className="text-sm text-gray-400">
                  Bot may be experiencing issues or slower response times
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Offline</h4>
                <p className="text-sm text-gray-400">
                  Bot is not responding and may be down for maintenance
                </p>
              </div>
            </div>
          </motion.div>



          {/* Footer Info */}
          <motion.div variants={fadeInUp} className="text-center">
            <div className="bg-slate-900/30 rounded-lg p-4 inline-flex items-center gap-2 text-sm text-gray-400">
              <Globe className="w-4 h-4" />
              <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
              <span>•</span>
              <span>Auto-refresh: 30s</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Status;